// IBISengage session design tokens (Figma "IBISengage" file, Design System page).
// Values are exact where SPEC.md section 1 lists them. Keep all new session UI on these tokens.

export const colors = {
  // UI colours
  blueGray: '#64748B',
  lightGray: '#B7C4D7',
  white: '#FFFFFF',
  lightTeal: '#EFF6FF', // dialog / menu background

  // Brand
  darkTeal: '#016069', // primary hover, active step number
  darkTealHover: 'rgba(0, 142, 155, 0.3)',
  accent: '#25BECB', // main swatch: links, active dots
  teal: '#259D90', // Teal Leaves: avatar circle, icon buttons
  primary: '#16727A', // Primary button (Main Brand 700)
  primaryHover: '#016069',
  primaryPressed: '#008E9B',

  brand: {
    50: '#E9F8FA',
    100: '#D3F2F5',
    200: '#A8E5EA',
    300: '#7CD8E0',
    400: '#51CBD5',
    500: '#259D90',
    600: '#1E98A2',
    700: '#16727A',
    800: '#0F4C51',
    900: '#072629',
  },
  secondaryBrand: {
    50: '#B0EFF5',
    100: '#9DE4EB',
    200: '#76CFD7',
    300: '#4EB9C3',
    400: '#27A4AF',
    500: '#008E9B',
    600: '#00727C',
    700: '#00555D',
    800: '#00393E',
    900: '#001C1F',
  },
  slate: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  success: { 50: '#ECFDF5', 100: '#D1FAE5', 200: '#A7F3D0', 500: '#10B981', 600: '#059669', 700: '#047857', 800: '#065F46' },
  warning: { 50: '#FFFBEB', 100: '#FEF3C7', 400: '#FBBF24', 500: '#F59E0B', 600: '#D97706', 700: '#B45309' },
  error: { 50: '#FFF1F2', 100: '#FFE4E6', 200: '#FECDD3', 500: '#F43F5E', 600: '#E11D48', 700: '#BE123C', 800: '#9F1239', 900: '#881337' },

  text: '#334155', // body text on white
  textMuted: '#64748B',
  heading: '#475569',
  pageBg: '#FFFFFF',
  tableHeader: '#4E9A96', // results table "Right Eye / Left Eye" teal header
  tableRowAlt: '#F3F4F6',
  notationBar: '#3A3A3A', // dark "< European Snellen >" row
  notApplicable: '#D1D1D1',
  trash: '#9B1C1C',

  // Video
  videoBg: '#13161B',
  namePlate: 'rgba(0, 0, 0, 0.9)',
  avatar: '#4A9A8A',
  micOffBadge: '#D0505F',
  controlSlate: '#64748B',
  scrim: 'rgba(51, 65, 85, 0.55)', // menu / dialog overlay (dims the page)
};

// Header gradients. `engage` is the NEW IBISengage header; `classic` the older Ibis-Connect one.
export const gradients = {
  engage: 'linear-gradient(90deg, #259B8F 0%, #1E6E75 100%)',
  classic: 'linear-gradient(90deg, #50D495 0%, #168294 100%)',
};

export const fonts = {
  family: "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

// Plus Jakarta Sans scale (size / line-height, px). Session app uses the larger touch sizes.
export const type = {
  h1: { size: 64, line: 72 },
  h2: { size: 48, line: 56 },
  h3: { size: 36, line: 44 },
  h4: { size: 30, line: 40 },
  h5: { size: 24, line: 32 },
  h6: { size: 20, line: 32 },
  headerTitle: { size: 28, line: 36 }, // session header screen title (Bold, white)
  p18: { size: 18, line: 28 },
  p16: { size: 16, line: 24 },
  p14: { size: 14, line: 20 },
  p12: { size: 12, line: 20 },
  p10: { size: 10, line: 12 },
};

export const weights = { regular: 400, medium: 500, semibold: 600, bold: 700 };

export const radii = {
  button: 5,
  buttonTertiary: 2,
  input: 5,
  dialog: 8,
  tile: 14, // video tiles (Figma 12–18)
  card: 10,
  table: 15,
  panel: 20, // "Screenshare Tile" main panel
  pill: 100,
};

export const shadows = {
  floating: '0 3.33px 5.23px rgba(0, 0, 0, 0.02)',
  panel: '0 3.33px 5.23px rgba(0, 0, 0, 0.15)',
  menu: '0 8px 20px rgba(0, 0, 0, 0.1)',
  dialog: '0 8px 24px rgba(15, 23, 42, 0.18)',
  toast: '0 8px 20px rgba(15, 23, 42, 0.12)',
};

export const space = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 };

// Figma frame sizes (CSS px) and the breakpoints derived from them.
export const frames = {
  ipadPortrait: { width: 744, height: 1133 },
  ipadLandscape: { width: 1133, height: 744 },
  iphone: { width: 375, height: 812 },
  desktopMaxWidth: 1180,
};

export const breakpoints = {
  phone: 600, // below: phone layout (iPhone frames)
  wide: 1024, // at/above in landscape: video column on the left
};

export const zIndex = { header: 50, menu: 200, drawer: 300, dialog: 400, toast: 500, blocker: 900 };

// 64px "Large" session buttons (Bold ~20) and the standard sizes from the Button component.
export const buttonSizes = {
  sm: { height: 28, padX: 8, font: 12 },
  md: { height: 40, padX: 16, font: 14 },
  lg: { height: 56, padX: 24, font: 16 },
  xl: { height: 64, padX: 28, font: 20 },
};

const tokens = { colors, gradients, fonts, type, weights, radii, shadows, space, frames, breakpoints, zIndex, buttonSizes };
export default tokens;
